import { Directive, Input, inject } from '@angular/core';
import { AbstractControl, ControlContainer } from '@angular/forms';

import { BehaviorSubject } from 'rxjs';
import { map, take } from 'rxjs/operators';

import {
    FieldDefinitionOption,
    FormElementStyle,
    FormElementType,
    FormFieldType,
    PendingChangeStatusType,
    RandomStringPipe,
    SelectApiSource,
    TranslationsService,
    WdxDestroyClass,
} from '@wdx/shared/utils';
import { PENDING_CHANGES_LOCK_FIELD } from '../constants';
import { IFormDynamicData } from '../interfaces';
import {
    ContextualDataContext,
    ReactiveFormLayoutAndDefinition,
} from '../models';
import { FormStaticService } from '../services';
import * as R from 'ramda';

@Directive()
export class BaseWdxFormControlClass extends WdxDestroyClass {
    protected translationsService = inject(TranslationsService);
    _formElement?: ReactiveFormLayoutAndDefinition;

    get formElement(): ReactiveFormLayoutAndDefinition {
        return this._formElement as ReactiveFormLayoutAndDefinition;
    }

    @Input() set formElement(value: ReactiveFormLayoutAndDefinition) {
        const selectSourceChange =
            this._formElement?.selectSource !== value?.selectSource;
        const contextualValuesChange = !R.equals(
            this._formElement?.contextualValues,
            value?.contextualValues,
        );
        this._formElement = value;

        this.formControl = this.controlContainer?.control?.get(
            value.name as string,
        ) as AbstractControl;

        if (this.formElement.options) {
            this.options$.next(
                this.filterInActiveOptions(this.formElement.options),
            );
        }

        if (
            this.formElement.selectSource &&
            (selectSourceChange || contextualValuesChange)
        ) {
            this.setOptionsForSelectSource();
        }

        if (value.pendingChange) {
            this.warningText =
                this.translationsService.translateTokenisedString(
                    'FORM_PENDING_CHANGE_FIELD_ERROR',
                );
        }
    }

    inputId = new RandomStringPipe().transform();

    options$ = new BehaviorSubject<FieldDefinitionOption[] | undefined>([]);

    readonly FORM_ELEMENT_STYLE = FormElementStyle;
    readonly FORM_ELEMENT_TYPE = FormElementType;
    readonly FormFieldType = FormFieldType;
    readonly PENDING_CHANGE_STATUS_TYPE = PendingChangeStatusType;
    readonly PENDING_CHANGES_LOCK_FIELD = PENDING_CHANGES_LOCK_FIELD;
    warningText: string | null = null;

    formControl?: AbstractControl;

    get isDisabled(): boolean | undefined {
        return (
            !!this.formStaticService.isReadonlyMode ||
            !!this.formElement?.isDisabled ||
            !!this.formElement?.isReadOnly
        );
    }

    protected formStaticService = inject(FormStaticService);

    constructor(
        public controlContainer: ControlContainer,
        public dynamicDataService: IFormDynamicData,
    ) {
        super();
    }

    setOptionsForSelectSource() {
        this.getOptionsForSelectSource$()
            .pipe(take(1))
            .subscribe((options) => this.options$.next(options));
    }

    getOptionsForContextSelectSource$(
        lookups: any[],
        context?: ContextualDataContext[],
    ) {
        return this.dynamicDataService
            .getContextSelectApiSourceOptions(
                this.formElement.selectSource as SelectApiSource,
                lookups,
                context,
            )
            .pipe(map((options) => this.filterInActiveOptions(options)));
    }

    getOptionsForSelectSource$(params = {}) {
        return this.dynamicDataService
            .getSelectApiSourceOptions(
                this.formElement.selectSource as SelectApiSource,
                params,
            )
            .pipe(map((options) => this.filterInActiveOptions(options)));
    }

    filterInActiveOptions(options: FieldDefinitionOption[] | undefined) {
        if (!options) {
            return;
        }
        const filteredOptions = options.filter(
            (option) =>
                option.value === this.formControl?.value ||
                option.active !== false,
        );

        return filteredOptions.map((option) => {
            const translatedOption = {
                ...option,
                label: this.translationsService.translateTokenisedString(
                    option.label as string,
                ),
            };
            return translatedOption as FieldDefinitionOption;
        });
    }
}
